import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

const ParametresTab = () => {
  const [entrepriseData, setEntrepriseData] = useState({
    raison_sociale: '',
    nif: '',
    rccm: '',
    adresse: '',
    contact_nom: '',
    contact_telephone: '',
    contact_email: '',
    wallet: ''
  });

  const [parametresAvance, setParametresAvance] = useState({
    plafond_pourcentage: 50,
    plafond_absolu: 2000000,
    delai_minimum: 0,
    taux_frais: 5,
    auto_approval: true,
    notification_email: true,
    notification_sms: false,
    periode_debut_jour: 1,
    periode_fin_jour: 31,
    periode_autorisation_active: false
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Charger les vraies données au montage
  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const api = (await import('../../lib/api')).default;
        
        // Charger le dashboard entreprise qui contient les parametres
        const response = await api.get('/api/entreprise/dashboard');
        
        if (response.data) {
          // Charger les paramètres d'avance depuis parametres_json
          const params = response.data.parametres_json || {};
          setParametresAvance({
            plafond_pourcentage: params.plafond_pourcentage || 50,
            plafond_absolu: params.plafond_absolu || 2000000,
            delai_minimum: params.delai_minimum || 0,
            taux_frais: params.taux_frais || 5,
            auto_approval: params.auto_approval !== undefined ? params.auto_approval : true,
            notification_email: params.notification_email !== undefined ? params.notification_email : true,
            notification_sms: params.notification_sms !== undefined ? params.notification_sms : false,
            periode_debut_jour: params.periode_debut_jour || 1,
            periode_fin_jour: params.periode_fin_jour || 31,
            periode_autorisation_active: params.periode_autorisation_active || false
          });
          
          // Charger les infos entreprise
          setEntrepriseData({
            raison_sociale: response.data.raison_sociale || '',
            nif: response.data.nif || '',
            rccm: response.data.rccm || '',
            adresse: response.data.adresse || '',
            contact_nom: response.data.contact_nom || '',
            contact_telephone: response.data.contact_telephone || '',
            contact_email: response.data.contact_email || '',
            wallet: response.data.wallet || ''
          });
        }
      } catch (error) {
        console.error('Erreur chargement paramètres:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handleSaveEntreprise = (e) => {
    e.preventDefault();
    // TODO: Appeler l'API pour sauvegarder
    console.log('Sauvegarde entreprise:', entrepriseData);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleSaveParametres = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const api = (await import('../../lib/api')).default;
      
      // Appeler l'API pour sauvegarder les paramètres
      await api.put('/api/entreprise/parametres', parametresAvance);
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
      alert(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Message de succès */}
      {showSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          ✅ Paramètres sauvegardés avec succès !
        </div>
      )}

      {/* Informations de l'entreprise */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <span className="mr-2">🏢</span>
            Informations de l'entreprise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveEntreprise} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison sociale
                </label>
                <Input
                  value={entrepriseData.raison_sociale}
                  onChange={(e) => setEntrepriseData({...entrepriseData, raison_sociale: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NIF
                </label>
                <Input
                  value={entrepriseData.nif}
                  onChange={(e) => setEntrepriseData({...entrepriseData, nif: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RCCM
                </label>
                <Input
                  value={entrepriseData.rccm}
                  onChange={(e) => setEntrepriseData({...entrepriseData, rccm: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wallet principal
                </label>
                <Input
                  value={entrepriseData.wallet}
                  onChange={(e) => setEntrepriseData({...entrepriseData, wallet: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <Input
                value={entrepriseData.adresse}
                onChange={(e) => setEntrepriseData({...entrepriseData, adresse: e.target.value})}
                required
              />
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Contact principal</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du contact
                  </label>
                  <Input
                    value={entrepriseData.contact_nom}
                    onChange={(e) => setEntrepriseData({...entrepriseData, contact_nom: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <Input
                    value={entrepriseData.contact_telephone}
                    onChange={(e) => setEntrepriseData({...entrepriseData, contact_telephone: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={entrepriseData.contact_email}
                    onChange={(e) => setEntrepriseData({...entrepriseData, contact_email: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button type="submit">
                💾 Sauvegarder les informations
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Paramètres des avances */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <span className="mr-2">⚙️</span>
            Paramètres des avances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveParametres} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Plafonds et limites</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plafond en % du salaire net
                    </label>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        value={parametresAvance.plafond_pourcentage}
                        onChange={(e) => setParametresAvance({...parametresAvance, plafond_pourcentage: parseInt(e.target.value)})}
                        min="10"
                        max="80"
                        className="w-20"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Recommandé: 50% maximum
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plafond absolu (GNF)
                    </label>
                    <Input
                      type="number"
                      value={parametresAvance.plafond_absolu}
                      onChange={(e) => setParametresAvance({...parametresAvance, plafond_absolu: parseInt(e.target.value)})}
                      min="100000"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Montant maximum qu'un employé peut demander
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Délai minimum entre demandes (jours)
                    </label>
                    <Input
                      type="number"
                      value={parametresAvance.delai_minimum}
                      onChange={(e) => setParametresAvance({...parametresAvance, delai_minimum: parseInt(e.target.value) || 0})}
                      min="0"
                      max="90"
                      className="w-20"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {parametresAvance.delai_minimum === 0 
                        ? "✅ Accès immédiat - Pas d'attente entre les demandes" 
                        : `Temps d'attente : ${parametresAvance.delai_minimum} jour(s)`
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Frais et commissions</h4>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-blue-900">Taux de commission ALO</h5>
                      <span className="text-2xl font-bold text-blue-800">{parametresAvance.taux_frais}%</span>
                    </div>
                    <p className="text-blue-700 text-sm mb-3">
                      Taux fixé par ALO FINANCES pour votre entreprise
                    </p>
                    <div className="bg-white border border-blue-200 rounded p-3">
                      <h6 className="font-medium text-blue-900 mb-1">Exemple de calcul</h6>
                      <div className="text-sm text-blue-700">
                        <div>Demande: 1.000.000 GNF</div>
                        <div>Frais ALO ({parametresAvance.taux_frais}%): {(1000000 * parametresAvance.taux_frais / 100).toLocaleString()} GNF</div>
                        <div>Net versé à l'employé: {(1000000 - (1000000 * parametresAvance.taux_frais / 100)).toLocaleString()} GNF</div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-blue-600">
                      💡 Pour modifier ce taux, contactez votre gestionnaire ALO FINANCES
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* NOUVELLE SECTION: Périodes de demande */}
            <div className="border-t pt-6">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                📅 Périodes de demande d'avance
                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Processus automatique
                </span>
              </h4>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <span className="text-yellow-600 text-lg">⚡</span>
                  <div>
                    <h5 className="font-medium text-yellow-900">Avantage ALO ADVANCE</h5>
                    <p className="text-yellow-800 text-sm">
                      Les demandes d'avance sont traitées instantanément sans validation manuelle. 
                      Par défaut, vos employés ont accès <strong>toute l'année sans restriction de période</strong>.
                      Vous pouvez optionnellement définir une période spécifique si nécessaire.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Restreindre les demandes à une période spécifique
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parametresAvance.periode_autorisation_active}
                        onChange={(e) => setParametresAvance({...parametresAvance, periode_autorisation_active: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  
                  {!parametresAvance.periode_autorisation_active && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
                      <p className="text-sm text-green-800">
                        ✅ <strong>Mode illimité activé</strong> - Vos employés peuvent faire des demandes toute l'année, tous les jours.
                      </p>
                    </div>
                  )}
                  
                  {parametresAvance.periode_autorisation_active && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Début de période (jour du mois)
                        </label>
                        <select
                          value={parametresAvance.periode_debut_jour}
                          onChange={(e) => setParametresAvance({...parametresAvance, periode_debut_jour: parseInt(e.target.value)})}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          {[...Array(28)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} de chaque mois
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fin de période (jour du mois)
                        </label>
                        <select
                          value={parametresAvance.periode_fin_jour}
                          onChange={(e) => setParametresAvance({...parametresAvance, periode_fin_jour: parseInt(e.target.value)})}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          {[...Array(28)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} de chaque mois
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h5 className="font-medium text-green-900 mb-3">Fonctionnement actuel</h5>
                  
                  {!parametresAvance.periode_autorisation_active ? (
                    <div className="space-y-2 text-sm text-green-800">
                      <div className="flex items-center space-x-2">
                        <span className="text-green-600 text-2xl">🔓</span>
                        <div>
                          <p className="font-semibold">Accès illimité</p>
                          <p>Vos employés peuvent faire des demandes <strong>tous les jours de l'année</strong></p>
                        </div>
                      </div>
                      <div className="mt-3 p-2 bg-white border border-green-200 rounded">
                        <p className="text-green-800">
                          ✅ Mode recommandé pour une flexibilité maximale
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm text-green-800">
                      <div className="flex items-center space-x-2">
                        <span className="text-green-600">✅</span>
                        <span>
                          Du <strong>{parametresAvance.periode_debut_jour}</strong> au <strong>{parametresAvance.periode_fin_jour}</strong> : 
                          Demandes autorisées
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600">❌</span>
                        <span>
                          Du <strong>{parametresAvance.periode_fin_jour + 1}</strong> au <strong>{parametresAvance.periode_debut_jour - 1}</strong> du mois suivant : 
                          Demandes bloquées
                        </span>
                      </div>
                      <div className="mt-3 p-2 bg-white border border-green-200 rounded">
                        <p className="font-medium text-green-900">Prochaine période :</p>
                        <p className="text-green-800">
                          {new Date().getMonth() < 11 ? 
                            `${parametresAvance.periode_debut_jour}/${String(new Date().getMonth() + 2).padStart(2, '0')} - ${parametresAvance.periode_fin_jour}/${String(new Date().getMonth() + 2).padStart(2, '0')}` :
                            `${parametresAvance.periode_debut_jour}/01 - ${parametresAvance.periode_fin_jour}/01`
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Workflow et notifications</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Traitement automatique ALO ADVANCE</p>
                    <p className="text-sm text-gray-600">
                      ✅ Les demandes sont traitées instantanément sans validation manuelle
                    </p>
                  </div>
                  <div className="text-green-600 font-medium text-sm bg-green-100 px-3 py-1 rounded-full">
                    Toujours activé
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Notifications email</p>
                    <p className="text-sm text-gray-600">
                      Recevoir les notifications par email
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parametresAvance.notification_email}
                      onChange={(e) => setParametresAvance({...parametresAvance, notification_email: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Notifications SMS</p>
                    <p className="text-sm text-gray-600">
                      Recevoir les notifications par SMS
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parametresAvance.notification_sms}
                      onChange={(e) => setParametresAvance({...parametresAvance, notification_sms: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button type="submit">
                💾 Sauvegarder les paramètres
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Gestion des utilisateurs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <span className="mr-2">👥</span>
              Utilisateurs de l'entreprise
            </span>
            <Button size="sm">
              ➕ Ajouter utilisateur
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Mamadou DIALLO</p>
                <p className="text-sm text-gray-600">admin@techsolutions.gn • Admin Entreprise</p>
              </div>
              <div className="flex space-x-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  Actif
                </span>
                <Button size="sm" variant="outline">✏️</Button>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Fatoumata BALDE</p>
                <p className="text-sm text-gray-600">rh@techsolutions.gn • RH</p>
              </div>
              <div className="flex space-x-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  Actif
                </span>
                <Button size="sm" variant="outline">✏️</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParametresTab;